// @flow
import { of } from 'rxjs';
import { catchError, mergeMap, map } from 'rxjs/operators';
import update from 'immutability-helper';
import { endpoints, getAddressesApi, postAddressApi, putAddressApi, deleteAddressApi } from './api';
import { setNewAlert } from '../../alert/duck';
import { ofType } from 'redux-observable';

import type { Observable } from 'rxjs';
import type { HTTPError } from '../../../error';
import type { Address } from '../../../entities';
import type { AddressRequest } from './api';

export type SavedAddressesAction = {
  +type: string;
  +address?: Address;
  +addresses?: Address[];
  +addressId?: string;
  +error?: HTTPError;
};

export type SavedAddressesState = {
  +isFetchingAddresses?: boolean;
  +isPostingAddress?: boolean;
  +isPuttingAddress?: boolean;
  +isDeletingAddress?: boolean;
  +addresses?: Address[];
  +closeAddressModal?: boolean;
  +successDelete?: boolean;
  +fetchAddressesError?: HTTPError;
  +postAddressError?: HTTPError;
  +editAddressError?: HTTPError;
  +deleteAddressError?: HTTPError;
};

// Actions
const SAVED_ADDRESSES_FETCH_ADDRESSES_REQUEST = 'SAVED_ADDRESSES_FETCH_ADDRESSES_REQUEST';
const SAVED_ADDRESSES_FETCH_ADDRESSES_SUCCESS = 'SAVED_ADDRESSES_FETCH_ADDRESSES_SUCCESS';
const SAVED_ADDRESSES_FETCH_ADDRESSES_ERROR = 'SAVED_ADDRESSES_FETCH_ADDRESSES_ERROR';
const SAVED_ADDRESSES_POST_ADDRESS_REQUEST = 'SAVED_ADDRESSES_POST_ADDRESS_REQUEST';
const SAVED_ADDRESSES_POST_ADDRESS_SUCCESS = 'SAVED_ADDRESSES_POST_ADDRESS_SUCCESS';
const SAVED_ADDRESSES_POST_ADDRESS_ERROR = 'SAVED_ADDRESSES_POST_ADDRESS_ERROR';
const SAVED_ADDRESSES_PUT_ADDRESS_REQUEST = 'SAVED_ADDRESSES_PUT_ADDRESS_REQUEST';
const SAVED_ADDRESSES_PUT_ADDRESS_SUCCESS = 'SAVED_ADDRESSES_PUT_ADDRESS_SUCCESS';
const SAVED_ADDRESSES_PUT_ADDRESS_ERROR = 'SAVED_ADDRESSES_PUT_ADDRESS_ERROR';
const SAVED_ADDRESSES_DELETE_ADDRESS_REQUEST = 'SAVED_ADDRESSES_DELETE_ADDRESS_REQUEST';
const SAVED_ADDRESSES_DELETE_ADDRESS_SUCCESS = 'SAVED_ADDRESSES_DELETE_ADDRESS_SUCCESS';
const SAVED_ADDRESSES_DELETE_ADDRESS_ERROR = 'SAVED_ADDRESSES_DELETE_ADDRESS_ERROR';

// Action creators
export function fetchAddresses(): SavedAddressesAction {
  return {
    type: SAVED_ADDRESSES_FETCH_ADDRESSES_REQUEST,
  }
}

export function fetchAddressesSuccess(addresses: Address[]): SavedAddressesAction {
  return {
    type: SAVED_ADDRESSES_FETCH_ADDRESSES_SUCCESS,
    addresses,
  }
}

export function fetchAddressesErrorFn(error: HTTPError): SavedAddressesAction {
  return {
    type: SAVED_ADDRESSES_FETCH_ADDRESSES_ERROR,
    error,
  }
}

export function postAddress(address: AddressRequest): SavedAddressesAction {
  return {
    type: SAVED_ADDRESSES_POST_ADDRESS_REQUEST,
    address,
  }
}

export function postAddressSuccess(address: Address): SavedAddressesAction {
  return {
    type: SAVED_ADDRESSES_POST_ADDRESS_SUCCESS,
    address,
  }
}

export function postAddressErrorFn(error: HTTPError): SavedAddressesAction {
  return {
    type: SAVED_ADDRESSES_POST_ADDRESS_ERROR,
    error,
  }
}

export function editAddress(address: Address, addressId: string): SavedAddressesAction {
  return {
    type: SAVED_ADDRESSES_PUT_ADDRESS_REQUEST,
    address,
    addressId
  }
}

export function editAddressSuccess(address: Address): SavedAddressesAction {
  return {
    type: SAVED_ADDRESSES_PUT_ADDRESS_SUCCESS,
    address,
  }
}

export function editAddressErrorFn(error: HTTPError): SavedAddressesAction {
  return {
    type: SAVED_ADDRESSES_PUT_ADDRESS_ERROR,
    error,
  }
}

export function deleteAddress(addressId: string): SavedAddressesAction {
  return {
    type: SAVED_ADDRESSES_DELETE_ADDRESS_REQUEST,
    addressId,
  }
}

export function deleteAddressSuccess(addressId: string): SavedAddressesAction {
  return {
    type: SAVED_ADDRESSES_DELETE_ADDRESS_SUCCESS,
    addressId,
  }
}

export function deleteAddressErrorFn(error: HTTPError): SavedAddressesAction {
  return {
    type: SAVED_ADDRESSES_DELETE_ADDRESS_ERROR,
    error,
  }
}

export const actions = {
  SAVED_ADDRESSES_FETCH_ADDRESSES_REQUEST,
  SAVED_ADDRESSES_FETCH_ADDRESSES_SUCCESS,
  SAVED_ADDRESSES_FETCH_ADDRESSES_ERROR,
  SAVED_ADDRESSES_POST_ADDRESS_REQUEST,
  SAVED_ADDRESSES_POST_ADDRESS_SUCCESS,
  SAVED_ADDRESSES_POST_ADDRESS_ERROR,
  SAVED_ADDRESSES_PUT_ADDRESS_REQUEST,
  SAVED_ADDRESSES_PUT_ADDRESS_SUCCESS,
  SAVED_ADDRESSES_PUT_ADDRESS_ERROR,
  SAVED_ADDRESSES_DELETE_ADDRESS_REQUEST,
  SAVED_ADDRESSES_DELETE_ADDRESS_SUCCESS,
  SAVED_ADDRESSES_DELETE_ADDRESS_ERROR,
  fetchAddresses,
  fetchAddressesSuccess,
  fetchAddressesErrorFn,
  postAddress,
  postAddressSuccess,
  postAddressErrorFn,
  editAddress,
  editAddressSuccess,
  editAddressErrorFn,
  deleteAddress,
  deleteAddressSuccess,
  deleteAddressErrorFn,
};

export const initialState: SavedAddressesState = {};

//Reducer
export default function reducer(state: SavedAddressesState = initialState, action: SavedAddressesAction): SavedAddressesState {
  switch (action.type) {
    case SAVED_ADDRESSES_FETCH_ADDRESSES_REQUEST:
      return {
        isFetchingAddresses: true,
      };
    case SAVED_ADDRESSES_FETCH_ADDRESSES_SUCCESS:
      return {
        addresses: action.addresses,
      };
    case SAVED_ADDRESSES_FETCH_ADDRESSES_ERROR:
      return {
        fetchAddressesError: action.error,
      };
    case SAVED_ADDRESSES_POST_ADDRESS_REQUEST:
      return update(state, {
        isPostingAddress: { $set: true },
        $unset: ['closeAddressModal'],
      });
    case SAVED_ADDRESSES_POST_ADDRESS_SUCCESS:
      return update(state, {
        $unset: ['isPostingAddress'],
        addresses: { $push: [action.address] },
        closeAddressModal: { $set: true },
      });
    case SAVED_ADDRESSES_POST_ADDRESS_ERROR:
      return update(state, {
        $unset: ['isPostingAddress', 'closeAddressModal'],
        postAddressError: { $set: action.error },
      });
    case SAVED_ADDRESSES_PUT_ADDRESS_REQUEST:
      return update(state, {
        isPuttingAddress: { $set: true },
        $unset: ['closeAddressModal'],
      });
    case SAVED_ADDRESSES_PUT_ADDRESS_SUCCESS:
      if (!state.addresses) return state;
      const putIndex = state.addresses.findIndex((address: Address) =>
        action.address ? address.id === action.address.id : -1
      );
      if (putIndex === -1) return state;
      return update(state, {
        $unset: ['isPuttingAddress'],
        addresses: { [putIndex]: { $merge: action.address } },
        closeAddressModal: { $set: true },
      });
    case SAVED_ADDRESSES_PUT_ADDRESS_ERROR:
      return update(state, {
        $unset: ['isPuttingAddress', 'closeAddressModal'],
        editAddressError: { $set: action.error }
      });
    case SAVED_ADDRESSES_DELETE_ADDRESS_REQUEST:
      return update(state, {
        $unset: ['deleteAddressErrorFn', 'successDelete'],
        isDeletingAddress: { $set: true },
      });
    case SAVED_ADDRESSES_DELETE_ADDRESS_SUCCESS:
      if (!state.addresses) return state;
      const deleteIndex = state.addresses.findIndex((address: Address) =>
        action.addressId === address.id);
      return update(state, {
        $unset: ['isDeletingAddress'],
        addresses: { $splice: [[deleteIndex, 1]] },
        successDelete: { $set: true },
      });
    case SAVED_ADDRESSES_DELETE_ADDRESS_ERROR:
      return update(state, {
        $unset: ['isDeletingAddress', 'successDelete'],
        deleteAddressError: { $set: action.error },
      });
    default:
      return state;
  }
}

// Epics
export function fetchAddressesEpic(action$: Observable<SavedAddressesAction>): Observable<SavedAddressesAction> {
  return action$.pipe(
    ofType(SAVED_ADDRESSES_FETCH_ADDRESSES_REQUEST),
    mergeMap(() => getAddressesApi().pipe(
      map((addresses: Address[]) => fetchAddressesSuccess(addresses)),
      catchError((e: HTTPError) => of(fetchAddressesErrorFn(e))))
    ));
}

export function postAddressEpic(action$: Observable<SavedAddressesAction>): Observable<SavedAddressesAction> {
  return action$.pipe(
    ofType(SAVED_ADDRESSES_POST_ADDRESS_REQUEST),
    mergeMap(({ address }) => {
      return postAddressApi(address).pipe(
        map((address: Address) => postAddressSuccess(address)),
        catchError((e: HTTPError) => {
          if (Math.round(e.code / 100) === 5) {
            return of(postAddressErrorFn(e),
              setNewAlert('preferences', 'error', undefined, 'httpErrors.post.500'),
            );
          } else if (e.code === 400) {
            return of(postAddressErrorFn(e),
              setNewAlert('preferences', 'error', undefined, 'httpErrors.post.400'),
            );
          }
          return of(postAddressErrorFn(e));
        }));
    }));
}

export function editAddressEpic(action$: Observable<SavedAddressesAction>): Observable<SavedAddressesAction> {
  return action$.pipe(
    ofType(SAVED_ADDRESSES_PUT_ADDRESS_REQUEST),
    mergeMap(({ address, addressId }) => {
      return putAddressApi(address, {
        route: endpoints.PUT_ADDRESS.replace(':id', addressId),
      }).pipe(
        map((response: Address) => editAddressSuccess(response)),
        catchError((e: HTTPError) => {
          if (Math.round(e.code / 100) === 5) {
            return of(editAddressErrorFn(e),
              setNewAlert('preferences', 'error', undefined, 'httpErrors.post.500'),
            );
          } else if (e.code === 400) {
            return of(editAddressErrorFn(e),
              setNewAlert('preferences', 'error', undefined, 'httpErrors.post.400'),
            );
          }
          return of(editAddressErrorFn(e));
        }));
    }));
}

export function deleteAddressEpic(action$: Observable<SavedAddressesAction>): Observable<SavedAddressesAction> {
  return action$.pipe(
    ofType(SAVED_ADDRESSES_DELETE_ADDRESS_REQUEST),
    mergeMap(({ addressId }) => deleteAddressApi(addressId, {
        route: endpoints.DELETE_ADDRESS.replace(':id', addressId),
      }).pipe(
      map(() => deleteAddressSuccess(addressId)),
      catchError((e: HTTPError) => {
        if (Math.round(e.code / 100) === 5) {
          return of(deleteAddressErrorFn(e),
            setNewAlert('preferences', 'error', undefined, 'httpErrors.post.500'),
          );
        } else if (e.code === 400) {
          return of(deleteAddressErrorFn(e),
            setNewAlert('preferences', 'error', undefined, 'httpErrors.post.400'),
          );
        }
        return of(deleteAddressErrorFn(e));
      }))
    ));
}



// WEBPACK FOOTER //
// ./src/modules/preferences/saved-addresses/duck.js